import React, { useState, useEffect } from 'react';
import Calling from '../../api/Calling';
import { toast } from 'react-toastify';
import Navbar from './Navbar';
import Loader from '../../components/Loader';
import NotFound from '../../components/ul/NotFound';
import formatDateTime from '../../components/formatDateTime';
import { imageType, invalidImageMessage, MAX_FILE_SIZE_IMAGE, imageSizeExceededMessage } from '../../components/imageType';
import { FaEye, FaUsersViewfinder } from "react-icons/fa6";
import { QRCodeSVG } from 'qrcode.react';

const WithdrawReq = () => {
    // Navigation function to replace useNavigate hook
    const navigateToUser = (userId) => {
        if (!userId) return; // Guard against undefined userId
        window.location.href = `/manage/user/all/info/${userId}`;
    };

    const [loader, setLoader] = useState(false);
    const [recordData, setRecordData] = useState([]);
    const [selectedTab, setSelectedTab] = useState('pending');
    const [page, setPage] = useState(1);
    const [limit] = useState(200);
    const [totalPages, setTotalPages] = useState(1);
    const [couponCode, setCouponCode] = useState('');
    const [couponExp, setCouponExp] = useState('');
    const [selectedRequestId, setSelectedRequestId] = useState(null);
    const [showAcceptModal, setShowAcceptModal] = useState(false);
    const [showRejectModal, setShowRejectModal] = useState(false);
    const [selectedRequests, setSelectedRequests] = useState([]);
    const [selectAll, setSelectAll] = useState(false);
    const [rejectReason, setRejectReason] = useState('');
    const [adminRejecting, setAdminRejecting] = useState('');
    const [upiImage, setUpiImage] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const [acceptError, setAcceptError] = useState('');
    // Image viewer state
    const [showImageViewer, setShowImageViewer] = useState(false);
    const [viewerImageUrl, setViewerImageUrl] = useState('');

    // Default rejection reason
    const defaultRejectReason = "Your redeem request was declined. Please try again later or contact support for assistance.";

    // Calculate default expiration date (30 days from now)
    const getDefaultExpirationDate = () => {
        const date = new Date();
        date.setDate(date.getDate() + 30);
        return date.toISOString().split('T')[0]; // Format as YYYY-MM-DD
    };

    // Set default expiration date when opening modal
    useEffect(() => {
        if (showAcceptModal) {
            setCouponExp(getDefaultExpirationDate());
        }

        // Set default rejection reason when opening reject modal
        if (showRejectModal) {
            setRejectReason(defaultRejectReason);
            // Set current admin name or ID (this should be retrieved from a context or state in a real app)
            setAdminRejecting('Admin');
        }
    }, [showAcceptModal, showRejectModal]);

    // Fetch data function with proper API path
    const fetchData = async () => {
        try {
            setLoader(true);
            // Use the updated function with correct path
            const resp = await Calling.getWithdrawHistory(page, limit, selectedTab);

            if (resp?.status) {
                // Ensure data is always an array
                const data = Array.isArray(resp?.data) ? resp?.data : [];
                setRecordData(data);

                // Handle string or number type for pagination values
                setTotalPages(parseInt(resp?.totalPage) || 1);

                // Parse page as number if it's returned as string
                if (resp.page && typeof resp.page === 'string') {
                    setPage(parseInt(resp.page));
                }
            } else {
                setRecordData([]);
                toast.error(resp?.message || 'Failed to fetch data');
            }
        } catch (e) {
            console.log(e);
            toast.error('Something went wrong while fetching data');
            setRecordData([]); // Ensure recordData is an array even on error
        } finally {
            setLoader(false);
        }
    };

    // Load data on component mount and when dependencies change
    useEffect(() => {
        fetchData();
        // Reset selections when tab changes
        setSelectedRequests([]);
        setSelectAll(false);
    }, [page, selectedTab]);

    // Handle pagination
    const handlePageChange = (newPage) => {
        setPage(newPage);
    };

    // Handle tab change
    const handleTabChange = (tab) => {
        setSelectedTab(tab);
        setPage(1);
    };

    // Handle view user details
    const handleViewUser = (userId) => {
        if (!userId) return; // Guard against undefined userId
        navigateToUser(userId);
    };

    // Handle accept withdrawal request
    const openAcceptModal = (id) => {
        if (!id) return; // Guard against undefined id
        setSelectedRequestId(id);
        setShowAcceptModal(true);
        // Reset image states when opening modal
        setUpiImage(null);
        setImagePreview(null);
        setAcceptError('');
        setCouponCode('');
    };

    // Handle image file selection
    const handleImageChange = (e) => {
        const file = e.target.files[0];

        if (!file) return;

        // Validate file type
        if (!imageType.includes(file.type)) {
            setAcceptError(invalidImageMessage);
            return;
        }

        // Validate file size
        if (file.size > MAX_FILE_SIZE_IMAGE) {
            setAcceptError(imageSizeExceededMessage);
            return;
        }

        // Clear any previous errors
        setAcceptError('');

        // Set the file for upload
        setUpiImage(file);

        // Create a preview
        const reader = new FileReader();
        reader.onloadend = () => {
            setImagePreview(reader.result);
        };
        reader.readAsDataURL(file);
    };

    const handleAcceptRequest = async () => {
        // Get the selected request object
        const selectedRequest = recordData.find(item => item._id === selectedRequestId);

        if (!selectedRequest) {
            toast.error('No request selected');
            return;
        }

        // Check if this is a UPI request
        const isUpiRequest = selectedRequest.couponType === 'UPI';

        // Create FormData for the API
        const formData = new FormData();

        // Validate based on request type
        if (isUpiRequest) {
            if (!upiImage) {
                setAcceptError('Please upload a UPI payment screenshot.');
                return;
            }

            // Add the image to the FormData for UPI requests
            formData.append('image', upiImage);

        } else {
            // For non-UPI requests, validate coupon code and expiration date
            if (!couponCode.trim()) {
                setAcceptError('Please enter a coupon code');
                return;
            }

            if (!couponExp) {
                setAcceptError('Please select an expiration date');
                return;
            }

            // Add coupon code and expiration date to FormData
            formData.append('couponCode', couponCode);

            // Create a Date object from the selected date, then convert to ISO string
            const expDate = new Date(couponExp);
            // Set the time to the end of day (23:59:59.999)
            expDate.setHours(23, 59, 59, 999);
            formData.append('couponExp', expDate.toISOString());
        }

        try {
            setLoader(true);

            // Call the unified API endpoint
            const resp = await Calling.acceptWithdrawRequest(selectedRequestId, formData);
            console.log('API Response:', resp);
            if (resp?.status) {
                toast.success(resp?.message || 'Request accepted successfully');
                fetchData();
                setShowAcceptModal(false);
                setCouponCode('');
                setCouponExp('');
                setUpiImage(null);
                setImagePreview(null);
                setAcceptError('');
            } else {
                toast.error(resp?.message || 'Failed to accept request');
            }
        } catch (e) {
            console.log(e);
            toast.error('Something went wrong');
        } finally {
            setLoader(false);
        }
    };

    // Handle reject withdrawal request
    const openRejectModal = (id) => {
        if (id) {
            setSelectedRequests([id]);
        }
        setShowRejectModal(true);
    };

    const handleRejectRequests = async () => {
        if (selectedRequests.length === 0) {
            toast.error('Please select at least one request to reject');
            return;
        }

        // Use default reason if none provided
        const finalReason = rejectReason.trim() || defaultRejectReason;
        try {
            setLoader(true);
            // Modified to send rejection reason, admin info, and timestamp
            const resp = await Calling.rejectWithdrawRequests(
                selectedRequests,
                finalReason,
            );

            if (resp?.status) {
                toast.success(resp?.message || 'Requests rejected successfully');
                fetchData();
                setShowRejectModal(false);
                setSelectedRequests([]);
                setSelectAll(false);
                setRejectReason('');
            } else {
                toast.error(resp?.message || 'Failed to reject requests');
            }
        } catch (e) {
            console.log(e);
            toast.error('Something went wrong');
        } finally {
            setLoader(false);
        }
    };

    // Handle select all checkbox
    const handleSelectAll = () => {
        if (selectAll) {
            setSelectedRequests([]);
        } else {
            // Select all valid items
            const validItems = recordData
                .filter(item => item?.status === 'pending')
                .map(item => item?._id)
                .filter(Boolean);

            setSelectedRequests(validItems);
        }
        setSelectAll(!selectAll);
    };

    // Handle single item selection
    const handleSelectItem = (id) => {
        if (!id) return; // Guard against undefined id

        if (selectedRequests.includes(id)) {
            setSelectedRequests(selectedRequests.filter(item => item !== id));
            setSelectAll(false);
        } else {
            setSelectedRequests([...selectedRequests, id]);

            // Check if all pending items are now selected
            const pendingItems = recordData.filter(item => item?.status === 'pending');
            if (selectedRequests.length + 1 === pendingItems.length) {
                setSelectAll(true);
            }
        }
    };

    // Format date function with safety checks
    const formatDate = (dateString) => {
        if (!dateString) return '-';

        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) return dateString || '-';

            // Format as DD/MM/YYYY or use locale-specific format
            return date.toLocaleDateString(undefined, {
                year: 'numeric',
                month: 'short',
                day: 'numeric'
            });
        } catch (e) {
            return dateString || '-';
        }
    };

    // Format time function with safety checks
    const formatTime = (dateString) => {
        if (!dateString) return '-';

        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) return '-';

            // Format as HH:MM AM/PM
            return date.toLocaleTimeString(undefined, {
                hour: '2-digit',
                minute: '2-digit'
            });
        } catch (e) {
            return '-';
        }
    };

    // Safely get a value with default
    const safeValue = (value, defaultValue = '') => {
        return value !== undefined && value !== null ? value : defaultValue;
    };

    const generateUpiUrl = (upiId, amount, note = 'Payment') => {
        // Format according to UPI deep linking specification
        return `upi://pay?pa=${encodeURIComponent(upiId)}&pn=Recipient&am=${encodeURIComponent(amount)}&cu=INR&tn=${encodeURIComponent(note)}`;
    };

    // Get user info safely
    const getUserInfo = (user) => {
        if (!user) return { id: '', name: '-', profile: null };

        // Handle both object and string formats for userId
        if (typeof user === 'object') {
            return {
                id: safeValue(user._id),
                name: safeValue(user.u_name, 'Unknown User'),
                profile: user.u_profile
            };
        }

        return { id: user, name: 'User ID: ' + user, profile: null };
    };

    // Get display for coin changes based on status
    const getCoinDisplay = (item) => {
        // If status is adminRejected, we show that coins were added back (+)
        if (item.status === 'adminRejected') {
            return (
                <div className="flex flex-col">
                    <span className="text-green-600">+{safeValue(item.coin, 0)}</span>
                    <span className="text-xs text-main-subfont">
                        Balance: {safeValue(item.h_coins, 0)}
                    </span>
                </div>
            );
        }
        // For all other statuses (rejected, completed, pending, etc.), show coins were deducted (-)
        else {
            return (
                <div className="flex flex-col">
                    <span className="text-red-600">-{safeValue(item.coin, 0)}</span>
                    <span className="text-xs text-main-subfont">
                        Balance: {safeValue(item.h_coins, 0)}
                    </span>
                </div>
            );
        }
    };

    // Get timestamp based on status
    const getStatusTimestamp = (item) => {
        if (!item) return { date: '-', time: '-' };

        if (item.status === 'completed' && item.completedAt) {
            return {
                date: formatDate(item.completedAt),
                time: formatTime(item.completedAt)
            };
        } else if ((item.status === 'rejected' || item.status === 'adminRejected') && item.rejectedAt) {
            return {
                date: formatDate(item.rejectedAt),
                time: formatTime(item.rejectedAt)
            };
        } else {
            return {
                date: formatDate(item.createdAt),
                time: formatTime(item.createdAt)
            };
        }
    };

    // Generate table based on request status
    const renderTable = () => {
        // Ensure recordData is always an array
        const safeRecordData = Array.isArray(recordData) ? recordData : [];

        return (
            <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-main-border">
                    <thead className="bg-main-th">
                        <tr>
                            {selectedTab === 'pending' && (
                                <th className="px-6 py-3 text-left text-xs font-medium text-main-font uppercase tracking-wider">
                                    <input
                                        type="checkbox"
                                        checked={selectAll}
                                        onChange={handleSelectAll}
                                        className="form-checkbox h-5 w-5 text-main-blue rounded border-main-border focus:ring-main-blue"
                                    />
                                </th>
                            )}
                            <th className="px-6 py-3 text-left text-xs font-medium text-main-font uppercase tracking-wider">User</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-main-font uppercase tracking-wider">Amount</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-main-font uppercase tracking-wider">Coins</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-main-font uppercase tracking-wider">Coupon</th>
                            {selectedTab === 'completed' && (
                                <>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-main-font uppercase tracking-wider">Coupon Code</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-main-font uppercase tracking-wider">Expires On</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-main-font uppercase tracking-wider">Update</th>
                                </>
                            )}
                            {selectedTab === 'rejected' && (
                                <>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-main-font uppercase tracking-wider">Reject Reason</th>
                                </>
                            )}

                            <th className="px-6 py-3 text-left text-xs font-medium text-main-font uppercase tracking-wider">Create</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-main-font uppercase tracking-wider">Status</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-main-font uppercase tracking-wider">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-main-border">
                        {safeRecordData.length > 0 ? (
                            safeRecordData.map((item, index) => {
                                // Skip rendering if item is null or undefined
                                if (!item) return null;

                                // Get data safely
                                const itemId = safeValue(item._id);
                                const user = getUserInfo(item.userId);
                                const itemStatus = safeValue(item.status, 'pending');
                                const isPendingItem = itemStatus === 'pending';
                                const statusTime = getStatusTimestamp(item);

                                return (
                                    <tr key={itemId || index} className="hover:bg-main-thHover">
                                        {selectedTab === 'pending' && (
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <input
                                                    type="checkbox"
                                                    checked={selectedRequests.includes(itemId)}
                                                    onChange={() => handleSelectItem(itemId)}
                                                    className="form-checkbox h-5 w-5 text-main-blue rounded border-main-border focus:ring-main-blue"
                                                    disabled={!itemId || !isPendingItem}
                                                />
                                            </td>
                                        )}
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <button
                                                onClick={() => handleViewUser(user.id)}
                                                className="flex items-center text-main-blue hover:text-main-yellow"
                                                disabled={!user.id}
                                            >
                                                {/* User profile picture */}
                                                {user.profile ? (
                                                    <div className="relative mr-2 flex-shrink-0">
                                                        <img
                                                            src={user.profile}
                                                            alt={user.name}
                                                            className="w-8 h-8 rounded-full object-cover"
                                                            onError={(e) => {
                                                                e.target.onerror = null;
                                                                e.target.src = "https://via.placeholder.com/32?text=User";
                                                            }}
                                                        />
                                                    </div>
                                                ) : (
                                                    <div className="w-8 h-8 rounded-full bg-main-blue flex items-center justify-center text-white mr-2 flex-shrink-0">
                                                        {user.name.charAt(0).toUpperCase()}
                                                    </div>
                                                )}

                                                {/* User name and ID */}
                                                <div className="flex flex-col">
                                                    <span className="font-medium text-sm truncate max-w-[120px]" title={user.name}>
                                                        {user.name}
                                                    </span>
                                                    <span className="text-xs text-main-subfont truncate max-w-[120px]" title={user.id}>
                                                        {user.id.substring(0, 8)}...
                                                    </span>
                                                </div>

                                                <svg
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    viewBox="0 0 20 20"
                                                    fill="currentColor"
                                                    className="w-4 h-4 ml-1 flex-shrink-0"
                                                >
                                                    <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                                                    <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" />
                                                </svg>
                                            </button>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            ₹{safeValue(item.amount, 0)}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            {getCoinDisplay(item)}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="flex items-center">
                                                {item.couponImage && (
                                                    <div className="relative group">
                                                        <img
                                                            src={item.couponImage}
                                                            alt="Coupon"
                                                            className="h-8 w-8 mr-2 rounded object-cover"
                                                            onError={(e) => {
                                                                e.target.onerror = null;
                                                                e.target.src = "https://via.placeholder.com/32";
                                                            }}
                                                        />
                                                        {/* Enlarged image on hover */}
                                                        <div className="absolute hidden group-hover:block left-0 top-0 transform -translate-y-full z-10">
                                                            <img
                                                                src={item.couponImage}
                                                                alt="Coupon Preview"
                                                                className="w-48 h-auto shadow-lg rounded border-2 border-main-blue"
                                                                onError={(e) => {
                                                                    e.target.onerror = null;
                                                                    e.target.src = "https://via.placeholder.com/200";
                                                                }}
                                                            />
                                                        </div>
                                                    </div>
                                                )}
                                                <span title={safeValue(item.couponTitle)} className="truncate max-w-[150px]">
                                                    {safeValue(item.couponTitle)}
                                                </span>
                                                {item.couponType === 'UPI' && (
                                                    <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-blue-100 text-blue-800">
                                                        UPI
                                                    </span>
                                                )}
                                            </div>
                                        </td>
                                        {selectedTab === 'completed' && (
                                            <>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <div className="flex items-center">
                                                        {item.couponType === 'UPI' ? (
                                                            <div className="relative group">
                                                                {item.upi_image ? (
                                                                    <button
                                                                        className="flex items-center gap-1 text-main-blue hover:text-main-yellow transition-colors"
                                                                        title="View UPI Payment Screenshot"
                                                                        onClick={() => {
                                                                            setViewerImageUrl(item.upi_image);
                                                                            setShowImageViewer(true);
                                                                        }}
                                                                    >
                                                                        <FaEye className="h-5 w-5" />
                                                                    </button>
                                                                ) : (
                                                                    <span className="text-main-subfont text-sm italic">No screenshot</span>
                                                                )}
                                                            </div>
                                                        ) : (
                                                            <>
                                                                <span className="font-medium text-main-blue">
                                                                    {safeValue(item.couponCode, '-')}
                                                                </span>
                                                                {item.couponCode && (
                                                                    <button
                                                                        onClick={() => {
                                                                            navigator.clipboard.writeText(item.couponCode);
                                                                            toast.success('Coupon code copied to clipboard!');
                                                                        }}
                                                                        className="ml-2 text-gray-500 hover:text-main-blue"
                                                                        title="Copy to clipboard"
                                                                    >
                                                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
                                                                        </svg>
                                                                    </button>
                                                                )}
                                                            </>
                                                        )}
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    {item.couponExp ? (
                                                        <div className="flex flex-col">
                                                            <span>{formatDate(item.couponExp)}</span>
                                                            {new Date(item.couponExp) < new Date() && (
                                                                <span className="text-xs text-red-600 font-medium">Expired</span>
                                                            )}
                                                        </div>
                                                    ) : '-'}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <div className="flex flex-col text-wrap">
                                                        <span>{formatDateTime(item?.updatedAt)}</span>
                                                    </div>
                                                </td>
                                            </>
                                        )}
                                        {(selectedTab === 'rejected' || selectedTab === 'adminRejected') && (
                                            <>
                                                <td className="px-6 py-4">
                                                    <div className="max-w-[200px] break-words">
                                                        {item.reason || '--'}
                                                    </div>
                                                </td>
                                            </>
                                        )}
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="flex flex-col">
                                                <span className='text-wrap'>{formatDateTime(item.createdAt)}</span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            {itemStatus === 'pending' ? (
                                                <span className="px-2 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800">
                                                    Pending
                                                </span>
                                            ) : itemStatus === 'completed' ? (
                                                <span className="px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                                                    Completed
                                                </span>
                                            ) : itemStatus === 'rejected' ? (
                                                <span className="px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">
                                                    Rejected
                                                </span>
                                            ) : itemStatus === 'adminRejected' ? (
                                                <span className="px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                                                    Admin Rejected
                                                </span>
                                            ) : (
                                                <span className="px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-800">
                                                    {itemStatus}
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            {isPendingItem ? (
                                                <div className="flex space-x-2">
                                                    <button
                                                        onClick={() => openAcceptModal(itemId)}
                                                        className="px-3 py-1 bg-main-blue text-white text-sm rounded hover:bg-opacity-90"
                                                        disabled={!itemId}
                                                    >
                                                        Accept
                                                    </button>
                                                    <button
                                                        onClick={() => openRejectModal(itemId)}
                                                        className="px-3 py-1 bg-main-delete text-white text-sm rounded hover:bg-opacity-90"
                                                        disabled={!itemId}
                                                    >
                                                        Reject
                                                    </button>
                                                </div>
                                            ) : (
                                                <button
                                                    onClick={() => handleViewUser(user.id)}
                                                    className="px-3 py-1 bg-main-blue text-white text-sm rounded hover:bg-opacity-90"
                                                    disabled={!user.id}
                                                >
                                                    View User
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })
                        ) : (
                            <tr>
                                <td colSpan={selectedTab === 'pending' ? 10 : selectedTab === 'rejected' ? 9 : 11} className="px-6 py-4 text-center text-main-subfont">
                                    <NotFound />
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        );
    };

    // Pagination component - Fixed pagination to handle string values
    const Pagination = () => {
        // Ensure we have valid numbers
        const currentPage = typeof page === 'number' ? page : 1;
        const totalPagesCount = typeof totalPages === 'number' ? totalPages : 1;

        return (
            <div className="flex items-center justify-between px-4 py-3 bg-white border-t border-main-border sm:px-6">
                <div className="flex justify-between flex-1 sm:hidden">
                    <button
                        onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
                        disabled={currentPage === 1}
                        className={`relative inline-flex items-center px-4 py-2 text-sm font-medium rounded-md ${currentPage === 1
                            ? 'text-gray-400 bg-white border border-main-border cursor-not-allowed'
                            : 'text-main-font bg-white border border-main-border hover:bg-main-th'
                            }`}
                    >
                        Previous
                    </button>
                    <button
                        onClick={() => handlePageChange(Math.min(totalPagesCount, currentPage + 1))}
                        disabled={currentPage === totalPagesCount}
                        className={`relative inline-flex items-center px-4 py-2 ml-3 text-sm font-medium rounded-md ${currentPage === totalPagesCount
                            ? 'text-gray-400 bg-white border border-main-border cursor-not-allowed'
                            : 'text-main-font bg-white border border-main-border hover:bg-main-th'
                            }`}
                    >
                        Next
                    </button>
                </div>
                <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
                    <div>
                        <p className="text-sm text-main-subfont">
                            Showing page <span className="font-medium">{currentPage}</span> of{' '}
                            <span className="font-medium">{totalPagesCount}</span>
                        </p>
                    </div>
                    <div>
                        <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                            <button
                                onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
                                disabled={currentPage === 1}
                                className={`relative inline-flex items-center px-2 py-2 rounded-l-md border border-main-border bg-white text-sm font-medium ${currentPage === 1
                                    ? 'text-gray-400 cursor-not-allowed'
                                    : 'text-main-font hover:bg-main-th'
                                    }`}
                            >
                                <span className="sr-only">Previous</span>
                                <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                                    <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                                </svg>
                            </button>

                            {/* Page Numbers - Guard against NaN */}
                            {totalPagesCount > 0 && [...Array(totalPagesCount)].map((_, i) => {
                                // Show only a limited number of page buttons
                                if (
                                    i + 1 === 1 ||
                                    i + 1 === totalPagesCount ||
                                    (i + 1 >= currentPage - 1 && i + 1 <= currentPage + 1)
                                ) {
                                    return (
                                        <button
                                            key={i}
                                            onClick={() => handlePageChange(i + 1)}
                                            className={`relative inline-flex items-center px-4 py-2 border border-main-border text-sm font-medium ${currentPage === i + 1
                                                ? 'z-10 bg-main-blue text-white border-main-blue'
                                                : 'bg-white text-main-font hover:bg-main-th'
                                                }`}
                                        >
                                            {i + 1}
                                        </button>
                                    );
                                } else if (
                                    (i + 1 === currentPage - 2 && currentPage > 3) ||
                                    (i + 1 === currentPage + 2 && currentPage < totalPagesCount - 2)
                                ) {
                                    return (
                                        <span
                                            key={i}
                                            className="relative inline-flex items-center px-4 py-2 border border-main-border bg-white text-sm font-medium text-main-subfont"
                                        >
                                            ...
                                        </span>
                                    );
                                }
                                return null;
                            })}

                            <button
                                onClick={() => handlePageChange(Math.min(totalPagesCount, currentPage + 1))}
                                disabled={currentPage === totalPagesCount}
                                className={`relative inline-flex items-center px-2 py-2 rounded-r-md border border-main-border bg-white text-sm font-medium ${currentPage === totalPagesCount
                                    ? 'text-gray-400 cursor-not-allowed'
                                    : 'text-main-font hover:bg-main-th'
                                    }`}
                            >
                                <span className="sr-only">Next</span>
                                <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                                    <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                                </svg>
                            </button>
                        </nav>
                    </div>
                </div>
            </div>
        );
    };
    const selectedRequest = recordData.find(item => item._id === selectedRequestId);
    const isUpiRequest = selectedRequest?.couponType === 'UPI';

    return (
        <div className="w-full text-main-font mt-16 px-3 md:ml-[4.4rem]">
            <h1 className="text-xl md:text-2xl font-bold mt-4 mb-2 text-main-font">Redeem Management</h1>
            <div className='bg-main-white rounded-xl shadow-lg overflow-hidden'>
                <Navbar selectState={'request'} />
                <hr className='border-main-border' />

                {/* Tab Navigation */}
                <div className="flex border-b border-main-border">
                    <button
                        className={`px-4 py-2 font-medium text-sm ${selectedTab === 'pending'
                            ? 'text-main-blue border-b-2 border-main-blue'
                            : 'text-main-subfont hover:text-main-font'
                            }`}
                        onClick={() => handleTabChange('pending')}
                    >
                        Pending Requests
                    </button>
                    <button
                        className={`px-4 py-2 font-medium text-sm ${selectedTab === 'completed'
                            ? 'text-main-blue border-b-2 border-main-blue'
                            : 'text-main-subfont hover:text-main-font'
                            }`}
                        onClick={() => handleTabChange('completed')}
                    >
                        Completed Requests
                    </button>
                    <button
                        className={`px-4 py-2 font-medium text-sm ${selectedTab === 'rejected'
                            ? 'text-main-blue border-b-2 border-main-blue'
                            : 'text-main-subfont hover:text-main-font'
                            }`}
                        onClick={() => handleTabChange('rejected')}
                    >
                        Rejected Requests
                    </button>
                    <button
                        className={`px-4 py-2 font-medium text-sm ${selectedTab === 'all'
                            ? 'text-main-blue border-b-2 border-main-blue'
                            : 'text-main-subfont hover:text-main-font'
                            }`}
                        onClick={() => handleTabChange('all')}
                    >
                        All Requests
                    </button>
                </div>

                {/* Bulk Actions (for pending tab) */}
                {selectedTab === 'pending' && selectedRequests.length > 0 && (
                    <div className="bg-main-th p-4 flex items-center justify-between">
                        <span className="text-sm text-main-font">
                            {selectedRequests.length} request{selectedRequests.length !== 1 ? 's' : ''} selected
                        </span>
                        <button
                            onClick={() => setShowRejectModal(true)}
                            className="px-3 py-1 bg-main-delete text-white text-sm rounded hover:bg-opacity-90"
                        >
                            Reject Selected
                        </button>
                    </div>
                )}

                {/* Table and Loader */}
                {loader ? (
                    <div className="py-10">
                        <Loader loader={loader} />
                    </div>
                ) : (
                    <>
                        {renderTable()}
                        {Array.isArray(recordData) && recordData.length > 0 && <Pagination />}
                    </>
                )}

                {/* Modals */}
                {showAcceptModal &&
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                        <div className="bg-white rounded-lg p-6 w-full max-w-md">
                            <h3 className="text-lg font-medium text-main-font mb-4">
                                Accept {isUpiRequest ? 'UPI ' : ''}Redeem Request
                            </h3>

                            {acceptError && (
                                <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-md text-sm">
                                    {acceptError}
                                </div>
                            )}

                            <div className="space-y-4">
                                {isUpiRequest ? (
                                    <div>
                                        <div className="flex justify-between items-center mb-2">
                                            <label className="block text-sm font-medium text-main-font mb-1">
                                                UPI Payment Screenshot <span className="text-red-500">*</span>
                                            </label>
                                            <span className="text-xs text-main-subfont">Max size: 1MB</span>
                                        </div>

                                        {imagePreview ? (
                                            <div className="relative mb-2">
                                                <img
                                                    src={imagePreview}
                                                    alt="Preview"
                                                    className="w-full h-auto max-h-40 object-contain border rounded-md"
                                                />
                                                <button
                                                    onClick={() => {
                                                        setUpiImage(null);
                                                        setImagePreview(null);
                                                    }}
                                                    className="absolute top-2 right-2 bg-main-delete text-white rounded-full p-1 shadow-md hover:bg-opacity-90"
                                                    title="Remove image"
                                                >
                                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                                        <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                                    </svg>
                                                </button>
                                            </div>
                                        ) : (
                                            <div className="border-2 border-dashed border-main-border rounded-md p-6 text-center hover:border-main-blue transition-colors">
                                                <input
                                                    type="file"
                                                    id="upiImage"
                                                    onChange={handleImageChange}
                                                    className="hidden"
                                                    accept=".jpg,.jpeg,.png"
                                                />
                                                <label htmlFor="upiImage" className="cursor-pointer flex flex-col items-center">
                                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 text-main-subfont mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                                    </svg>
                                                    <span className="text-sm text-main-font font-medium">Click to upload image</span>
                                                    <span className="text-xs text-main-subfont mt-1">PNG, JPG, JPEG (Max 1MB)</span>
                                                </label>
                                            </div>
                                        )}

                                        <div className="flex items-center justify-between mt-2">
                                            <p className="text-xs text-main-subfont">
                                                Upload a screenshot showing the transaction details for:
                                            </p>
                                        </div>
                                        {selectedRequest?.upi_id && (
                                            <div className="mt-3">
                                                <div className="flex items-center justify-between mb-2">
                                                    <span className="text-sm font-medium text-main-font">UPI ID:</span>
                                                    <div className="flex items-center">
                                                        <span className="text-sm font-medium text-main-font break-all mr-2">
                                                            {selectedRequest.upi_id}
                                                        </span>
                                                        <button
                                                            onClick={() => {
                                                                navigator.clipboard.writeText(selectedRequest.upi_id);
                                                                toast.success('UPI ID copied to clipboard!');
                                                            }}
                                                            className="px-2 py-1 text-xs bg-main-blue text-white rounded hover:bg-opacity-90"
                                                            title="Copy UPI ID"
                                                        >
                                                            Copy
                                                        </button>
                                                    </div>
                                                </div>

                                                {/* UPI QR Code */}
                                                <div className="flex flex-col items-center bg-gray-50 p-3 rounded-md border border-main-border">
                                                    <div className="bg-white p-2 rounded-md shadow-sm mb-2">
                                                        <QRCodeSVG
                                                            value={generateUpiUrl(selectedRequest.upi_id, selectedRequest.amount, 'Redeem Payment')}
                                                            size={200}
                                                            level="H"
                                                            includeMargin={true}
                                                        />
                                                    </div>
                                                    <p className="text-xs text-center text-main-subfont mb-2">
                                                        Scan with any UPI app to pay ₹{selectedRequest.amount} directly to {selectedRequest.upi_id}
                                                    </p>
                                                    <button
                                                        onClick={() => {
                                                            const upiUrl = generateUpiUrl(selectedRequest.upi_id, selectedRequest.amount, 'Redeem Payment');
                                                            window.open(upiUrl, '_blank');
                                                            toast.info('Opening UPI payment app...');
                                                        }}
                                                        className="px-3 py-1 bg-green-600 text-white text-sm rounded hover:bg-green-700"
                                                    >
                                                        Pay Now
                                                    </button>
                                                </div>
                                            </div>
                                        )}

                                    </div>
                                ) : (
                                    <>
                                        <div>
                                            <label className="block text-sm font-medium text-main-font mb-1">
                                                Coupon Code <span className="text-red-500">*</span>
                                            </label>
                                            <input
                                                type="text"
                                                name="couponCode"
                                                value={couponCode}
                                                onChange={(e) => setCouponCode(e.target.value)}
                                                placeholder="Enter coupon code"
                                                className="w-full px-3 py-2 border border-main-border rounded-md focus:outline-none focus:ring-2 focus:ring-main-blue"
                                                autoFocus
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-main-font mb-1">
                                                Expiration Date <span className="text-red-500">*</span>
                                            </label>
                                            <input
                                                type="date"
                                                name="couponExp"
                                                value={couponExp}
                                                onChange={(e) => setCouponExp(e.target.value)}
                                                min={new Date().toISOString().split('T')[0]}
                                                className="w-full px-3 py-2 border border-main-border rounded-md focus:outline-none focus:ring-2 focus:ring-main-blue"
                                            />
                                            <p className="text-xs text-main-subfont mt-1">
                                                The coupon will expire at the end of this day (midnight).
                                            </p>
                                        </div>
                                    </>
                                )}
                            </div>

                            <div className="flex justify-end space-x-3 mt-6">
                                <button
                                    onClick={() => {
                                        setShowAcceptModal(false);
                                        setCouponCode('');
                                        setCouponExp('');
                                        setUpiImage(null);
                                        setImagePreview(null);
                                        setAcceptError('');
                                    }}
                                    className="px-4 py-2 text-main-font bg-white border border-main-border rounded-md hover:bg-main-th"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleAcceptRequest}
                                    className="px-4 py-2 text-white bg-main-blue rounded-md hover:bg-opacity-90"
                                >
                                    Accept
                                </button>
                            </div>
                        </div>
                    </div>
                }

                {showRejectModal &&
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                        <div className="bg-white rounded-lg p-6 w-full max-w-md">
                            <h3 className="text-lg font-medium text-main-font mb-4">Reject Redeem Request</h3>
                            <div className="space-y-4">
                                <p className="text-main-subfont">
                                    Are you sure you want to reject {selectedRequests.length === 1 ? 'this' : 'these'} {selectedRequests.length} redeem request{selectedRequests.length !== 1 ? 's' : ''}?
                                    The coins will be returned to the user's account.
                                </p>

                                <div>
                                    <label className="block text-sm font-medium text-main-font mb-1">
                                        Rejection Reason
                                    </label>
                                    <textarea
                                        value={rejectReason}
                                        onChange={(e) => setRejectReason(e.target.value)}
                                        placeholder="Enter reason for rejection (optional)"
                                        className="w-full px-3 py-2 border border-main-border rounded-md focus:outline-none focus:ring-2 focus:ring-main-blue min-h-[80px]"

                                    />
                                    <p className="text-xs text-main-subfont mt-1">
                                        If left empty, a default reason will be used.
                                    </p>
                                </div>
                            </div>
                            <div className="flex justify-end space-x-3 mt-6">
                                <button
                                    onClick={() => {
                                        setShowRejectModal(false);
                                        setRejectReason('');
                                        if (selectedRequests.length === 1 && !selectAll) {
                                            setSelectedRequests([]);
                                        }
                                    }}
                                    className="px-4 py-2 text-main-font bg-white border border-main-border rounded-md hover:bg-main-th"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleRejectRequests}
                                    className="px-4 py-2 text-white bg-main-delete rounded-md hover:bg-opacity-90"
                                >
                                    Reject
                                </button>
                            </div>
                        </div>
                    </div>
                }

                {/* Image Viewer Modal */}
                {showImageViewer && (
                    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-75 z-50">
                        <div className="relative bg-white p-3 rounded-lg shadow-xl max-w-3xl w-full mx-4">
                            <img
                                src={viewerImageUrl}
                                alt="UPI Payment Screenshot"
                                className="w-full h-auto max-h-[80vh] object-contain rounded"
                                onError={(e) => {
                                    e.target.onerror = null;
                                    e.target.src = "https://via.placeholder.com/400?text=UPI+Screenshot";
                                }}
                            />
                            <button
                                onClick={() => setShowImageViewer(false)}
                                className="absolute top-2 right-2 bg-red-600 text-white rounded-full p-1.5 hover:bg-red-700 transition-colors shadow-md"
                                title="Close"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default WithdrawReq;